import React, { useEffect, useMemo } from 'react';

import { selectClassName, SELECT_BACKGROUND } from '../FormField';

import { Choice, Group, RadioChoice, Toggle } from './OptionControls';

import MultiSelectField from 'CommonComponents/MultiSelectField';

const GROUPS = [
    { key: 'central', label: () => $t('Central plan') },
    { key: 'departmental', label: () => $t('Departments') },
    { key: 'costcentre', label: () => $t('Cost centres') },
    { key: 'venue', label: () => $t('Venues') },
];

const HORIZONS = [30, 60, 90];

/**
 * The plans the picker offers: those in the ticked groups, grouped by group.
 */
const pickerOptions = (plans, groups) =>
    GROUPS.filter((group) => groups[group.key])
        .map((group) => ({
            label: group.label(),
            options: plans.filter((plan) => plan.group === group.key).map((plan) => ({ value: plan.value, label: plan.label })),
        }))
        .filter((group) => group.options.length > 0);

/**
 * The project plan section's options: which plans and activity types the
 * pages cover, which pages they include, and how far ahead they look.
 */
const PlansOptions = ({ options, plans, readOnly, onChange }) => {
    // The parent passes new plan and option objects on every render, so the offered plans are keyed by their values.
    const choices = pickerOptions(plans, options.plan_groups);
    const offeredKey = choices
        .flatMap((group) => group.options.map((option) => option.value))
        .sort((a, b) => a - b)
        .join(',');
    const available = useMemo(() => new Set(offeredKey === '' ? [] : offeredKey.split(',').map(Number)), [offeredKey]);

    const set = (changes) => onChange({ ...options, ...changes });

    // A plan the picker no longer offers, say after the scope changed, is dropped so no hidden choice stays applied.
    // Runs only when the offered plans change: `options` and `set` are read fresh from that render, and listing them
    // would re-run it on every render, since the parent rebuilds both each time.
    useEffect(() => {
        if (readOnly) {
            return;
        }

        const kept = options.plan_ids.filter((id) => available.has(id));

        if (kept.length !== options.plan_ids.length) {
            set({ plan_ids: kept });
        }
    }, [available, readOnly]);

    const toggleGroup = (key) => set({ plan_groups: { ...options.plan_groups, [key]: !options.plan_groups[key] } });

    const noGroups = !GROUPS.some((group) => options.plan_groups[group.key]);
    const noPages = !options.status_page && !options.activities_page;

    return (
        <div className="tw-flex tw-flex-col tw-mt-3 tw-ps-4 tw-border-s-2 tw-border-charcoal-15">
            <Group title={$t('Plans to include')}>
                {GROUPS.map((group) => (
                    <Toggle
                        key={group.key}
                        label={group.label()}
                        checked={!!options.plan_groups[group.key]}
                        disabled={readOnly}
                        onChange={() => toggleGroup(group.key)}
                    />
                ))}
                {noGroups && <div className="tw-text-red-500 tw-text-sm">{$t('Select at least one group of plans.')}</div>}
                {!noGroups && (
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                        <label className="tw-text-sm tw-text-charcoal tw-mb-0" htmlFor="mp-plans-picker">
                            {$t('Specific plans')}
                        </label>
                        <MultiSelectField
                            inputId="mp-plans-picker"
                            className={selectClassName}
                            backgroundColor={SELECT_BACKGROUND}
                            options={choices}
                            isGrouped
                            hideSelectedOptions={false}
                            clearable
                            isDisabled={readOnly}
                            value={options.plan_ids.filter((id) => available.has(id))}
                            placeholder={$t('All plans in the ticked groups')}
                            onChange={(selected) => set({ plan_ids: (selected || []).map((option) => option.value) })}
                        />
                    </div>
                )}
            </Group>

            <Group title={$t('Activities to report')}>
                <RadioChoice
                    name="mp-plans-activity-types"
                    label={$t('Activities to report')}
                    value={options.activity_types}
                    choices={[
                        { value: 'key_milestones', label: $t('Key milestones only') },
                        { value: 'milestones', label: $t('All milestones') },
                        { value: 'all', label: $t('All activities') },
                    ]}
                    disabled={readOnly}
                    onChange={(value) => set({ activity_types: value })}
                />
            </Group>

            <Group title={$t('Pages')}>
                <Toggle
                    label={$t('Plan status')}
                    checked={!!options.status_page}
                    disabled={readOnly}
                    onChange={() => set({ status_page: !options.status_page })}
                />
                <Toggle
                    label={$t('Activities')}
                    checked={!!options.activities_page}
                    disabled={readOnly}
                    onChange={() => set({ activities_page: !options.activities_page })}
                />
                {noPages && <div className="tw-text-red-500 tw-text-sm">{$t('With both pages off, the section has no pages.')}</div>}
                {options.activities_page && (
                    <Choice
                        id="mp-plans-horizon"
                        label={$t('Forward look')}
                        value={options.horizon_days}
                        choices={HORIZONS.map((days) => ({ value: days, label: $t('{{count}} days', { count: days }) }))}
                        disabled={readOnly}
                        onChange={(value) => set({ horizon_days: value })}
                    />
                )}
            </Group>
        </div>
    );
};

export default PlansOptions;
